import { useChart } from '@chakra-ui/charts';
import { Center, Text, useToken } from '@chakra-ui/react';
import ChartWrapper from '@components/ui/chart/ChartWrapper';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectAltitudeUnit } from '@redux/settings/settings.selectors';
import { convertDistanceUnitFromMeter } from '@utils/unit.utils';
import { useIntl } from 'react-intl';
import { Bar, BarChart, CartesianGrid, Tooltip, TooltipContentProps, XAxis, YAxis } from 'recharts';

import { AltitudeRangeDroneCountType } from '@/types/data/statistic.types';

type Props = {
  data: AltitudeRangeDroneCountType[];
};

const CustomGraphTooltip = ({ active, payload }: TooltipContentProps) => {
  const isVisible = active && payload.length !== 0;
  if (!isVisible) {
    return;
  }
  return (
    <Center paddingY={1} paddingX={1.5} backgroundColor="neutral.black">
      <Text>{Number(payload[0].value)}</Text>
    </Center>
  );
};

export default function DroneByAltitudeGraph({ data }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const [neutral300, neutral700, skyBlue] = useToken('colors', ['neutral.300', 'neutral.700', 'skyBlue']);
  const [fontSmall] = useToken('fontSizes', ['xs']);

  const altitudeUnit = useAppSelector(selectAltitudeUnit);
  const chart = useChart({
    data: data.map((entry) => {
      const to = entry.to === null ? '+' : `-${convertDistanceUnitFromMeter(altitudeUnit, entry.to, 0)}`;
      return { range: `${convertDistanceUnitFromMeter(altitudeUnit, entry.from, 0)}${to}`, value: entry.count };
    }),
  });

  return (
    <ChartWrapper
      width="480px"
      isEmpty={chart.getTotal('value') === 0}
      emptyLabel={formatMessage({ id: 'components.notificationSearch.reporting.noDetection' })}
      title={formatMessage(
        { id: 'components.notificationSearch.reporting.droneByAltitude' },
        {
          unit: formatMessage({ id: `units.${altitudeUnit}` }),
        },
      )}
    >
      <BarChart style={{ width: '100%', height: '100%' }} data={chart.data} responsive barSize={16}>
        <CartesianGrid strokeWidth={2} stroke={neutral700} vertical={false} />
        <XAxis
          axisLine={false}
          tickLine={false}
          dataKey="range"
          interval={0}
          tick={{
            fill: neutral300,
            style: { fontSize: fontSmall },
          }}
        />
        <Tooltip content={(props) => <CustomGraphTooltip {...props} />} cursor={{ fill: neutral700 }} />
        <YAxis
          axisLine={false}
          tickLine={false}
          domain={[0, 'dataMax']}
          niceTicks="adaptive"
          width={40}
          tick={{
            fill: neutral300,
          }}
        />
        <Bar dataKey="value" fill={skyBlue} activeBar={false} />
      </BarChart>
    </ChartWrapper>
  );
}
